/** Tax planning: the firm's plans, and the plan workspace (figures, scenarios, projection, review, delivery). */
import { businessOwnerMissing, FILING_STATUS_NAMES, formatCents, MD_LOCAL_RATE_RANGE, PLANNING_CORRECTED_ON, PLANNING_QUESTIONS, roundHalfAwayFromZero, STRATEGIES, type FilingStatus, type Strategy, type StrategyKey, type TaxInputs, type YearResult } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, FileText, Plus, Sparkles, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Link, useLocation } from "wouter";
import { ask, askText } from "@/components/dialogs";
import { Empty, ListSearch, Loading, PageHeader, Pager, Section, StatRow, useServerPaged } from "@/components/page";
import { PlanningGuide, PlanningOpportunities, PlanningRequests, StartPlanningDialog } from "@/components/planning";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime, plural } from "@/lib/format";
import { useMemberNames } from "@/lib/members";
import { hasPermission, type Me } from "@/lib/session";
import type { Doc } from "@/lib/types";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

const select = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";
const usd = (n: number) => formatCents(roundHalfAwayFromZero(n) * 100).replace(/\.00$/, "");
/** Dollars that may carry cents (an estimate's last installment). */
const usdCents = (n: number) => formatCents(roundHalfAwayFromZero(n * 100)).replace(/\.00$/, "");
const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
const COLORS = ["var(--viz-1)", "var(--viz-2)", "var(--viz-3)", "var(--viz-4)", "var(--viz-5)"];

type PlanStatus = "draft" | "in_review" | "approved" | "shared" | "accepted";
const STATUS: Record<PlanStatus, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "bg-muted text-muted-foreground" },
  in_review: { label: "In review", cls: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200" },
  approved: { label: "Approved", cls: "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200" },
  shared: { label: "Shared with client", cls: "bg-indigo-100 text-indigo-900 dark:bg-indigo-900/30 dark:text-indigo-200" },
  accepted: { label: "Accepted", cls: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200" },
};
export function PlanStatusBadge({ status }: { status: PlanStatus }) {
  return <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS[status].cls)}>{STATUS[status].label}</span>;
}

type PlanRow = { id: string; clientId: string; clientName: string; title: string; baseYear: number; years: number; status: PlanStatus; questionnaire: { sentAt: string; submittedAt: string | null } | null; updatedAt: string; acceptedAt: string | null };
type StateInputs = { code: "MD" | "VA" | "DC" | "flat" | "none"; localRatePct: number; flatRatePct: number; subtractions: number; withholding?: number | null };
const STATE_NAMES: Record<string, string> = { MD: "Maryland", VA: "Virginia", DC: "DC" };
type ScenarioStrategy = { key: StrategyKey; params: Record<string, number>; years: number[] };
type Scenario = { id: string; name: string; position: number; strategies: ScenarioStrategy[]; results: YearResult[]; total: number };
type PlanDetail = {
  plan: {
    id: string;
    clientId: string;
    title: string;
    baseYear: number;
    years: number;
    incomeGrowthPct: number;
    inflationPct: number;
    inputs: { federal: TaxInputs; state: StateInputs };
    sources: Record<string, string>;
    priorYearTax: number | null;
    priorYearAgi: number | null;
    questionnaire: { sentAt: string; submittedAt: string | null; answers: Record<string, unknown> } | null;
    summary: string;
    recommendedScenarioId: string | null;
    status: PlanStatus;
    createdByUserId: string | null;
    reviewedAt: string | null;
    reviewedByUserId: string | null;
    sharedAt: string | null;
    acceptedAt: string | null;
    acceptedName: string | null;
  };
  clientName: string;
  scenarios: Scenario[];
  tables: Array<{ year: number; estimated: boolean; sources: string[] }>;
  /** What the latest engine corrections changed in this open plan (empty when none applies). */
  corrections?: Array<{ key: string; years: number[]; text: string }>;
};
type Library = { strategies: Strategy[]; limitations: string[] };

// --- Firm-wide list --------------------------------------------------------------------------

export function TaxPlanningPage({ me }: { me: Me }) {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("view") ?? "plans");
  const [starting, setStarting] = useState(false);
  const { data: reqs } = useQuery<{ total: number }>({ queryKey: ["/api/tax-planning/requests?limit=1"] });
  const pick = (v: string) => {
    setTab(v);
    const u = new URL(window.location.href);
    if (v === "plans") u.searchParams.delete("view");
    else u.searchParams.set("view", v);
    window.history.replaceState(null, "", u);
  };
  return (
    <>
      <PageHeader
        crumbs={[{ label: "Tax", href: "/tax" }]}
        title="Tax Planning"
        description="Find the clients who would benefit, start planning as an engagement with its fee, build and compare strategies, and deliver a plan the client accepts."
        actions={
          hasPermission(me, "engagements.write") && (
            <Button onClick={() => setStarting(true)}>
              <Plus className="h-4 w-4" /> Start planning
            </Button>
          )
        }
      />
      {starting && <StartPlanningDialog target={null} me={me} onClose={() => setStarting(false)} />}
      <PlanningGuide />
      <Tabs value={tab} onValueChange={pick}>
        <TabsList className="mb-4 h-auto flex-wrap justify-start">
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="suggested">Suggested</TabsTrigger>
          <TabsTrigger value="requests">Client requests{reqs?.total ? ` (${reqs.total})` : ""}</TabsTrigger>
        </TabsList>
        <TabsContent value="plans">
          <PlansList />
        </TabsContent>
        <TabsContent value="suggested">
          <PlanningOpportunities me={me} />
        </TabsContent>
        <TabsContent value="requests">
          <PlanningRequests me={me} />
        </TabsContent>
      </Tabs>
    </>
  );
}

function PlansList() {
  const [status, setStatus] = useState<PlanStatus | "">("");
  const [search, setSearch] = useState("");
  const term = search.trim();
  const sp = useServerPaged(`${status}|${term}`);
  const qs = new URLSearchParams({ ...(status ? { status } : {}), ...(term ? { q: term } : {}), offset: String(sp.offset), limit: String(sp.size) });
  const { data, isLoading } = useQuery<{ items: PlanRow[]; total: number; counts: Partial<Record<PlanStatus, number>> }>({ queryKey: [`/api/tax-plans?${qs}`], placeholderData: (prev) => prev });
  const rows = data?.items ?? [];
  const c = data?.counts ?? {};
  const all = Object.values(c).reduce((n, x) => n + (x ?? 0), 0);
  const tile = (s: PlanStatus) => ({ selected: status === s, onClick: () => setStatus(status === s ? "" : s) });
  return (
    <>
      <StatRow
        stats={[
          { label: "Drafts", value: c.draft ?? 0, ...tile("draft") },
          { label: "Waiting for review", value: c.in_review ?? 0, tone: c.in_review ? "warning" : "default", ...tile("in_review") },
          { label: "With clients", value: c.shared ?? 0, ...tile("shared") },
          { label: "Accepted", value: c.accepted ?? 0, tone: "success", ...tile("accepted") },
        ]}
      />
      {all > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select aria-label="Status" className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value as PlanStatus | "")}>
            <option value="">All statuses ({all})</option>
            {(Object.keys(STATUS) as PlanStatus[]).map((k) => (
              <option key={k} value={k}>
                {STATUS[k].label} ({c[k] ?? 0})
              </option>
            ))}
          </select>
          <ListSearch value={search} onChange={setSearch} placeholder="Search client or plan title" />
        </div>
      )}
      {isLoading ? (
        <Loading />
      ) : !rows.length ? (
        <Empty>{all === 0 ? 'No tax plans yet. Choose "Start planning", or start one from the Suggested or Client requests tab.' : "No tax plans match."}</Empty>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-card-border bg-card">
          {rows.map((p) => (
            <li key={p.id}>
              <Link href={`/tax/planning/${p.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 hover:bg-muted/30">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{p.clientName}</span>
                  <span className="text-xs text-muted-foreground">
                    {p.title} · {p.baseYear}
                    {p.years > 1 && `–${p.baseYear + p.years - 1}`}
                    {p.questionnaire && (p.questionnaire.submittedAt ? " · questionnaire answered" : " · questionnaire sent")}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">Updated {formatDate(p.updatedAt.slice(0, 10))}</span>
                <PlanStatusBadge status={p.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {data && <Pager paged={sp.paged(data.total)} noun="plans" className="mt-3" />}
    </>
  );
}

/** A client's plans on their Tax tab, with "New tax plan". */
export function ClientTaxPlans({ clientId, clientName, me }: { clientId: string; clientName?: string; me: Me }) {
  const { data: res } = useQuery<{ items: PlanRow[]; total: number }>({ queryKey: [`/api/tax-plans?clientId=${clientId}`] });
  const data = res?.items;
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      {hasPermission(me, "engagements.write") && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Start planning
          </Button>
        </div>
      )}
      {!data?.length ? (
        <Empty>No tax plans. A plan projects up to five years, compares strategies, and is accepted by the client in the portal.</Empty>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {data.map((p) => (
            <li key={p.id}>
              <Link href={`/tax/planning/${p.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{p.title}</span>
                <PlanStatusBadge status={p.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {open && <StartPlanningDialog target={{ clientId, name: clientName ?? "this client" }} me={me} onClose={() => setOpen(false)} />}
    </div>
  );
}

// --- Figures ---------------------------------------------------------------------------------

/** "tpWages" and "tpDeferrals" are the taxpayer's part on a joint plan (the couple's total less the spouse's). */
type FieldKey = keyof TaxInputs | "tpWages" | "tpDeferrals";
type Field = { key: FieldKey; label: string; hint?: string };
/** On a joint plan each spouse's wages and deferrals are entered separately (the Social Security wage base is per person). */
const JOINT_PAY: Field[] = [
  { key: "tpWages", label: "Taxpayer's wages (W-2 box 1)" },
  { key: "spouseWages", label: "Spouse's wages (W-2 box 1)" },
  { key: "tpDeferrals", label: "Taxpayer's pre-tax 401(k) deferrals" },
  { key: "spouseDeferrals", label: "Spouse's pre-tax 401(k) deferrals" },
];
const GROUPS: Array<{ title: string; fields: Field[] }> = [
  {
    title: "Income",
    fields: [
      { key: "wages", label: "Wages (W-2 box 1)" },
      { key: "deferrals", label: "Pre-tax 401(k) deferrals" },
      { key: "taxableInterest", label: "Taxable interest" },
      { key: "taxExemptInterest", label: "Tax-exempt interest" },
      { key: "ordinaryDividends", label: "Ordinary dividends" },
      { key: "qualifiedDividends", label: "Qualified dividends (part of ordinary)" },
      { key: "shortTermGains", label: "Short-term gains (losses negative)" },
      { key: "longTermGains", label: "Long-term gains (losses negative)" },
      { key: "capitalLossCarryover", label: "Capital loss carryover" },
      { key: "retirementDistributions", label: "IRA and pension distributions (taxable)" },
      { key: "socialSecurity", label: "Social Security benefits (total)" },
      { key: "rentalIncome", label: "Rental income (net)" },
      { key: "otherIncome", label: "Other income" },
    ],
  },
  {
    title: "Business",
    fields: [
      { key: "businessIncome", label: "Schedule C net profit" },
      { key: "businessW2Wages", label: "W-2 wages the business pays (for QBI)" },
      { key: "sCorpIncome", label: "S corporation K-1 income" },
      { key: "partnershipSeIncome", label: "Partnership K-1 income (self-employment)" },
      { key: "passThroughW2Wages", label: "K-1 business W-2 wages (for QBI)" },
      { key: "qualifiedTips", label: "Qualified tips" },
      { key: "qualifiedOvertime", label: "Qualified overtime premium" },
    ],
  },
  {
    title: "Adjustments and deductions",
    fields: [
      { key: "iraDeduction", label: "Deductible IRA contributions" },
      { key: "hsaDeduction", label: "HSA contributions" },
      { key: "seHealthInsurance", label: "Self-employed health insurance" },
      { key: "sepContribution", label: "SEP / solo 401(k) employer contribution" },
      { key: "studentLoanInterest", label: "Student loan interest" },
      { key: "stateLocalTaxes", label: "State and local taxes paid (income and property)" },
      { key: "mortgageInterest", label: "Mortgage interest" },
      { key: "charityCash", label: "Cash gifts to charity" },
      { key: "charityNonCash", label: "Non-cash gifts to charity" },
      { key: "medical", label: "Medical expenses" },
      { key: "isoBargainElement", label: "ISO bargain element (AMT)" },
    ],
  },
  {
    title: "Payments",
    fields: [
      { key: "withholding", label: "Federal withholding" },
      { key: "estimatedPayments", label: "Estimated payments made" },
    ],
  },
];

/** Maps a line of last year's return onto the plan. */
const RETURN_MAP: Record<string, { field?: keyof TaxInputs; prior?: "priorYearTax" | "priorYearAgi" }> = {
  wages: { field: "wages" },
  taxableInterest: { field: "taxableInterest" },
  taxExemptInterest: { field: "taxExemptInterest" },
  ordinaryDividends: { field: "ordinaryDividends" },
  qualifiedDividends: { field: "qualifiedDividends" },
  iraDistributions: { field: "retirementDistributions" },
  socialSecurity: { field: "socialSecurity" },
  capitalGain: { field: "longTermGains" },
  businessIncome: { field: "businessIncome" },
  rentalAndK1: { field: "sCorpIncome" },
  agi: { prior: "priorYearAgi" },
  totalTax: { prior: "priorYearTax" },
  withholding: { field: "withholding" },
  estimatedPayments: { field: "estimatedPayments" },
};

function ReadReturnDialog({ plan, onClose }: { plan: PlanDetail["plan"]; onClose: () => void }) {
  const { data: docs } = useQuery<Doc[]>({ queryKey: ["/api/documents?clientId=", plan.clientId] });
  const [doc, setDoc] = useState("");
  const [lines, setLines] = useState<Array<{ key: string; label: string; text: string | null; amount: number | null; use: boolean }> | null>(null);
  const read = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ lines: Array<{ key: string; label: string; text: string | null; amount: number | null }> }>("POST", `/api/tax-plans/${plan.id}/read-return`, { documentId: doc }),
    onSuccess: (r) => setLines(r.lines.map((l) => ({ ...l, use: l.amount !== null && !!RETURN_MAP[l.key] }))),
  });
  const apply = useMutation({
    meta: { inlineError: true },
    mutationFn: () => {
      const federal: Record<string, number | string> = {};
      const sources: Record<string, string> = {};
      const body: Record<string, unknown> = {};
      const year = lines?.find((l) => l.key === "taxYear")?.text ?? "prior-year";
      for (const l of lines ?? []) {
        const m = RETURN_MAP[l.key];
        if (!l.use || l.amount === null || !m) continue;
        if (m.field) {
          federal[m.field] = (Number(federal[m.field] ?? 0) || 0) + l.amount;
          sources[m.field] = `${year} Form 1040`;
        }
        if (m.prior) body[m.prior] = l.amount;
      }
      const fs = lines?.find((l) => l.key === "filingStatus")?.text;
      if (fs && ["single", "mfj", "mfs", "hoh", "qss"].includes(fs)) federal.filingStatus = fs === "qss" ? "mfj" : fs;
      return api.send("PATCH", `/api/tax-plans/${plan.id}`, { ...body, federal, sources });
    },
    onSuccess: onClose,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Read last year's return</DialogTitle>
        </DialogHeader>
        {!lines ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Choose the client's prior-year Form 1040 (PDF or scan). The figures are read for you to check; nothing is used until you confirm.</p>
            <select className={select} value={doc} onChange={(e) => setDoc(e.target.value)} aria-label="Return document">
              <option value="">Choose a document</option>
              {(docs ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.filename}
                </option>
              ))}
            </select>
            {read.error && <p className="text-sm text-destructive">{errorMessage(read.error)}</p>}
            <div className="flex justify-end">
              <Button onClick={() => read.mutate()} disabled={!doc || read.isPending}>
                {read.isPending ? "Reading…" : "Read the return"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Check each amount against the return. Ticked lines replace the plan's figures; last year's AGI and total tax set the estimate safe harbor.</p>
            <ul className="divide-y divide-border rounded-md border border-border">
              {lines.map((l, i) => (
                <li key={l.key} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <input type="checkbox" aria-label={`Use ${l.label}`} disabled={l.amount === null || !RETURN_MAP[l.key]} checked={l.use} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, use: e.target.checked } : x)))} />
                  <span className="min-w-0 flex-1 text-muted-foreground">{l.label}</span>
                  <span className="text-right font-medium tabular">{l.amount !== null ? usd(l.amount) : (l.text ?? "—")}</span>
                </li>
              ))}
            </ul>
            {apply.error && <p className="text-sm text-destructive">{errorMessage(apply.error)}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={() => apply.mutate()} disabled={apply.isPending}>
                Use the ticked figures
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function QuickBooksDialog({ plan, onClose }: { plan: PlanDetail["plan"]; onClose: () => void }) {
  // Businesses the client owns (Related clients on their record) are offered first; any other
  // bookkeeping client is found by name.
  const { data: detail } = useQuery<{ relationships: Array<{ type: string; fromClientId: string; toClientId: string }>; related: Array<{ id: string; displayName: string; kind: string }> }>({ queryKey: ["/api/clients/", plan.clientId] });
  const owned = new Set((detail?.relationships ?? []).filter((r) => r.type === "owner_of" && r.fromClientId === plan.clientId).map((r) => r.toClientId));
  const ownedClients = (detail?.related ?? []).filter((c) => owned.has(c.id));
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<{ id: string; displayName: string } | null>(null);
  const { data: found } = useQuery<{ items: Array<{ id: string; displayName: string }> }>({ queryKey: [`/api/clients?module=bookkeeping&limit=20&q=${encodeURIComponent(q.trim())}`], enabled: q.trim().length > 0 });
  const [v, setV] = useState({ businessClientId: "", ownershipPct: "100", treatment: "sCorpIncome" as "businessIncome" | "sCorpIncome" | "partnershipSeIncome" });
  const [r, setR] = useState<{ through: string; months: number; ytdNetIncome: number; annualized: number; ownerShare: number } | null>(null);
  const pull = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send<typeof r>("POST", `/api/tax-plans/${plan.id}/quickbooks`, { businessClientId: v.businessClientId, ownershipPct: Number(v.ownershipPct) }), onSuccess: setR });
  const apply = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("PATCH", `/api/tax-plans/${plan.id}`, { federal: { [v.treatment]: r!.ownerShare }, sources: { [v.treatment]: `QuickBooks through ${r!.through}, annualized` } }),
    onSuccess: onClose,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Business profit from QuickBooks</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="qb-biz">Business</Label>
            {picked ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-input px-3 py-2 text-sm">
                <span className="truncate font-medium">
                  {picked.displayName}
                  {owned.has(picked.id) && <span className="font-normal text-muted-foreground"> (owned by the client)</span>}
                </span>
                <button type="button" className="text-xs text-primary hover:underline" onClick={() => (setPicked(null), setR(null), setV({ ...v, businessClientId: "" }))}>
                  Change
                </button>
              </div>
            ) : (
              <>
                {ownedClients.length > 0 && (
                  <ul className="rounded-md border border-border text-sm">
                    {ownedClients.map((c) => (
                      <li key={c.id}>
                        <button type="button" className="w-full px-3 py-2 text-left hover:bg-muted" onClick={() => (setPicked(c), setV({ ...v, businessClientId: c.id }))}>
                          {c.displayName} <span className="text-muted-foreground">(owned by the client)</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <Input id="qb-biz" placeholder="Search bookkeeping clients by name" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
                {q.trim().length > 0 && (
                  <ul className="max-h-48 overflow-y-auto rounded-md border border-border text-sm">
                    {(found?.items ?? []).map((c) => (
                      <li key={c.id}>
                        <button type="button" className="w-full px-3 py-2 text-left hover:bg-muted" onClick={() => (setPicked(c), setV({ ...v, businessClientId: c.id }))}>
                          {c.displayName}
                        </button>
                      </li>
                    ))}
                    {found && !found.items.length && <li className="px-3 py-2 text-muted-foreground">No bookkeeping client matches.</li>}
                  </ul>
                )}
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="qb-own">Ownership %</Label>
              <Input id="qb-own" inputMode="decimal" value={v.ownershipPct} onChange={(e) => setV({ ...v, ownershipPct: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="qb-as">Taxed as</Label>
              <select id="qb-as" className={select} value={v.treatment} onChange={(e) => setV({ ...v, treatment: e.target.value as typeof v.treatment })}>
                <option value="businessIncome">Sole proprietorship (Schedule C)</option>
                <option value="sCorpIncome">S corporation</option>
                <option value="partnershipSeIncome">Partnership</option>
              </select>
            </div>
          </div>
          {r && (
            <div className="rounded-md bg-muted/40 p-3 text-sm">
              Net income through {formatDate(r.through)}: <span className="font-medium tabular">{usd(r.ytdNetIncome)}</span> ({plural(r.months, "month")}), annualized <span className="font-medium tabular">{usd(r.annualized)}</span>. Owner's share: <span className="font-semibold tabular">{usd(r.ownerShare)}</span>.
            </div>
          )}
          {(pull.error || apply.error) && <p className="text-sm text-destructive">{errorMessage(pull.error ?? apply.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => pull.mutate()} disabled={!v.businessClientId || pull.isPending}>
              Read QuickBooks
            </Button>
            <Button onClick={() => apply.mutate()} disabled={!r || apply.isPending}>
              Use this figure
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A field saved when it loses focus. It shows the server's value again whenever that changes (an
 * import or another field's save), and sends nothing when the text is unchanged.
 */
function BlurInput({ value, onCommit, ...props }: { value: string; onCommit: (text: string) => void } & Omit<React.ComponentProps<typeof Input>, "value" | "defaultValue" | "onBlur">) {
  return <Input key={value} defaultValue={value} onBlur={(e) => e.target.value.trim() !== value && onCommit(e.target.value)} {...props} />;
}

const money = (n: number | null | undefined) => (n == null ? "" : n.toLocaleString("en-US"));
const wholeOrNull = (text: string) => (text.trim() ? Math.round(Number(text.replace(/[$,\s]/g, "")) || 0) : null);

function Figures({ d, canEdit }: { d: PlanDetail; canEdit: boolean }) {
  const p = d.plan;
  const f = p.inputs.federal;
  const [draft, setDraft] = useState<Record<string, string>>({});
  const save = useMutation({ mutationFn: (body: Record<string, unknown>) => api.send("PATCH", `/api/tax-plans/${p.id}`, body), onSuccess: (_r, body) => {
      // Only the field that was saved; anything being typed elsewhere stays.
      const saved = Object.keys((body.federal as Record<string, unknown> | undefined) ?? {});
      if (saved.includes("wages")) saved.push("tpWages");
      if (saved.includes("deferrals")) saved.push("tpDeferrals");
      setDraft((cur) => Object.fromEntries(Object.entries(cur).filter(([k]) => !saved.includes(k))));
    },
  });
  const [dialog, setDialog] = useState<"return" | "qbo" | null>(null);
  const joint = f.filingStatus === "mfj";
  const spouseWages = f.spouseWages ?? 0;
  const spouseDeferrals = f.spouseDeferrals ?? 0;
  /** A field's figure: the taxpayer's part on a joint plan is the couple's total less the spouse's. */
  const valueOf = (key: FieldKey): number => (key === "tpWages" ? f.wages - spouseWages : key === "tpDeferrals" ? f.deferrals - spouseDeferrals : Number(f[key]) || 0);
  const sourceOf = (key: FieldKey) => p.sources[key === "tpWages" ? "wages" : key === "tpDeferrals" ? "deferrals" : key];
  const commit = (key: FieldKey) => {
    const raw = draft[key];
    if (raw === undefined) return;
    const n = roundHalfAwayFromZero(Number(raw.replace(/[$,\s]/g, "")) || 0);
    if (n === valueOf(key)) {
      setDraft((cur) => Object.fromEntries(Object.entries(cur).filter(([k]) => k !== key)));
      return;
    }
    const by = "Entered by the firm";
    // The couple's totals stay the sum of each person's figures.
    if (key === "tpWages") save.mutate({ federal: { wages: n + spouseWages }, sources: { wages: by } });
    else if (key === "tpDeferrals") save.mutate({ federal: { deferrals: n + spouseDeferrals }, sources: { deferrals: by } });
    else if (key === "spouseWages") save.mutate({ federal: { spouseWages: n, wages: valueOf("tpWages") + n }, sources: { spouseWages: by } });
    else if (key === "spouseDeferrals") save.mutate({ federal: { spouseDeferrals: n, deferrals: valueOf("tpDeferrals") + n }, sources: { spouseDeferrals: by } });
    else save.mutate({ federal: { [key]: n }, sources: { [key]: by } });
  };
  const groups = joint ? GROUPS.map((g) => (g.title === "Income" ? { ...g, fields: [...JOINT_PAY, ...g.fields.filter((x) => x.key !== "wages" && x.key !== "deferrals")] } : g)) : GROUPS;
  const ownerSelect = (id: string, field: "businessOwner" | "partnershipOwner", label: string) => (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <select id={id} className={select} disabled={!canEdit} value={f[field] ?? ""} onChange={(e) => save.mutate({ federal: { [field]: e.target.value || null } })}>
        <option value="">Not said</option>
        <option value="taxpayer">Taxpayer</option>
        <option value="spouse">Spouse</option>
      </select>
    </div>
  );
  const q = p.questionnaire;
  const applyAnswers = useMutation({
    mutationFn: () => {
      const federal: Record<string, number> = {};
      const sources: Record<string, string> = {};
      for (const question of PLANNING_QUESTIONS) {
        const a = q?.answers[question.key];
        if (question.maps && typeof a === "number") {
          federal[question.maps] = Math.round(a);
          sources[question.maps] = "Client questionnaire";
        }
      }
      return api.send("PATCH", `/api/tax-plans/${p.id}`, { federal, sources });
    },
  });
  return (
    <Section
      title={`Figures for ${p.baseYear}`}
      description="Each figure shows where it came from. Later years grow by the assumption below."
      action={
        canEdit && (
          <div className="flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setDialog("return")}>
              <FileText className="h-4 w-4" /> Read last year's return
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDialog("qbo")}>
              From QuickBooks
            </Button>
          </div>
        )
      }
    >
      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1">
          <Label htmlFor="pf-status">Filing status</Label>
          <select id="pf-status" className={select} disabled={!canEdit} value={f.filingStatus} onChange={(e) => save.mutate({ federal: { filingStatus: e.target.value, ages: e.target.value === "mfj" ? [f.ages[0] ?? 45, f.ages[1] ?? f.ages[0] ?? 45] : [f.ages[0] ?? 45] } })}>
            {Object.entries(FILING_STATUS_NAMES).map(([k, n]) => (
              <option key={k} value={k}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="pf-ages">{joint ? `Ages at the end of ${p.baseYear} (taxpayer, spouse)` : `Age at the end of ${p.baseYear}`}</Label>
          <BlurInput id="pf-ages" disabled={!canEdit} value={f.ages.join(", ")} onCommit={(text) => save.mutate({ federal: { ages: text.split(",").map((x) => Number(x.trim())).filter((x) => x > 0).slice(0, f.filingStatus === "mfj" ? 2 : 1) } })} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pf-kids">Children under 17</Label>
          <BlurInput id="pf-kids" inputMode="numeric" disabled={!canEdit} value={String(f.childrenUnder17)} onCommit={(text) => save.mutate({ federal: { childrenUnder17: Number(text) || 0 } })} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pf-state">State</Label>
          <select
            id="pf-state"
            className={select}
            disabled={!canEdit}
            value={p.inputs.state.code}
            onChange={(e) => {
              // Maryland needs a county rate in its legal range; start from the default if the plan's isn't.
              const lr = p.inputs.state.localRatePct;
              save.mutate({ state: { code: e.target.value, ...(e.target.value === "MD" && !(lr >= MD_LOCAL_RATE_RANGE.min && lr <= MD_LOCAL_RATE_RANGE.max) ? { localRatePct: 3.2 } : {}) } });
            }}
          >
            <option value="MD">Maryland</option>
            <option value="VA">Virginia</option>
            <option value="DC">District of Columbia</option>
            <option value="flat">Other state (flat rate)</option>
            <option value="none">No state income tax</option>
          </select>
        </div>
        {p.inputs.state.code === "MD" && (
          <div className="space-y-1">
            <Label htmlFor="pf-local">County tax rate % (2.25 to 3.30)</Label>
            <BlurInput id="pf-local" inputMode="decimal" disabled={!canEdit} value={String(p.inputs.state.localRatePct ?? "")} onCommit={(text) => save.mutate({ state: { localRatePct: Number(text) || 0 } })} />
          </div>
        )}
        {(p.inputs.state.code === "MD" || p.inputs.state.code === "VA" || p.inputs.state.code === "DC") && (
          <div className="space-y-1">
            <Label htmlFor="pf-swh">{STATE_NAMES[p.inputs.state.code]} withholding</Label>
            <BlurInput id="pf-swh" inputMode="numeric" disabled={!canEdit} placeholder="Not entered" value={money(p.inputs.state.withholding)} onCommit={(text) => save.mutate({ state: { withholding: wholeOrNull(text) } })} />
          </div>
        )}
        {p.inputs.state.code === "flat" && (
          <div className="space-y-1">
            <Label htmlFor="pf-flat">State rate % of AGI</Label>
            <BlurInput id="pf-flat" inputMode="decimal" disabled={!canEdit} value={String(p.inputs.state.flatRatePct ?? "")} onCommit={(text) => save.mutate({ state: { flatRatePct: Number(text) || 0 } })} />
          </div>
        )}
        <div className="space-y-1">
          <Label htmlFor="pf-py">Last year's total tax</Label>
          <BlurInput id="pf-py" inputMode="numeric" disabled={!canEdit} value={money(p.priorYearTax)} onCommit={(text) => save.mutate({ priorYearTax: wholeOrNull(text) })} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pf-pagi">Last year's AGI</Label>
          <BlurInput id="pf-pagi" inputMode="numeric" disabled={!canEdit} value={money(p.priorYearAgi)} onCommit={(text) => save.mutate({ priorYearAgi: wholeOrNull(text) })} />
        </div>
      </div>
      {q?.submittedAt && (
        <div className="mb-5 rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-medium">The client answered the questionnaire on {formatDate(q.submittedAt.slice(0, 10))}.</p>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => applyAnswers.mutate()} disabled={applyAnswers.isPending}>
                Use their figures
              </Button>
            )}
          </div>
          <dl className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
            {PLANNING_QUESTIONS.filter((x) => q.answers[x.key] !== undefined && q.answers[x.key] !== null && q.answers[x.key] !== "").map((x) => (
              <div key={x.key} className="text-xs">
                <dt className="text-muted-foreground">{x.label}</dt>
                <dd className="font-medium">{typeof q.answers[x.key] === "number" ? usd(q.answers[x.key] as number) : String(q.answers[x.key])}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        {groups.map((g) => (
          <fieldset key={g.title} className="min-w-0">
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.title}</legend>
            <div className="divide-y divide-border rounded-md border border-border">
              {g.fields.map((fl) => (
                <label key={fl.key} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{fl.label}</span>
                    {sourceOf(fl.key) && <span className="block truncate text-[11px] text-muted-foreground">{sourceOf(fl.key)}</span>}
                  </span>
                  <Input
                    className="h-8 w-32 text-right tabular"
                    inputMode="numeric"
                    disabled={!canEdit}
                    value={draft[fl.key] ?? (valueOf(fl.key) ? valueOf(fl.key).toLocaleString("en-US") : "")}
                    placeholder="0"
                    onChange={(e) => setDraft({ ...draft, [fl.key]: e.target.value })}
                    onBlur={() => commit(fl.key)}
                    onKeyDown={(e) => e.key === "Enter" && commit(fl.key)}
                  />
                </label>
              ))}
            </div>
          </fieldset>
        ))}
        <fieldset className="min-w-0">
          <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Business details</legend>
          <div className="space-y-2 rounded-md border border-border p-3 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" disabled={!canEdit} checked={f.businessIsSstb} onChange={(e) => save.mutate({ federal: { businessIsSstb: e.target.checked } })} /> Schedule C business is a specified service business (law, health, consulting, accounting…)
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" disabled={!canEdit} checked={f.passThroughIsSstb} onChange={(e) => save.mutate({ federal: { passThroughIsSstb: e.target.checked } })} /> K-1 business is a specified service business
            </label>
            {joint && (
              <div className="grid gap-3 pt-1 sm:grid-cols-2">
                {ownerSelect("pf-bowner", "businessOwner", "Schedule C business (and its salary) belongs to")}
                {ownerSelect("pf-powner", "partnershipOwner", "Partnership self-employment income belongs to")}
              </div>
            )}
          </div>
        </fieldset>
      </div>
      {save.error && <p className="mt-3 text-sm text-destructive">{errorMessage(save.error)}</p>}
      {dialog === "return" && <ReadReturnDialog plan={p} onClose={() => setDialog(null)} />}
      {dialog === "qbo" && <QuickBooksDialog plan={p} onClose={() => setDialog(null)} />}
    </Section>
  );
}

/** Shown on a joint plan that has wages and business income but doesn't say whose business it is. */
function OwnerNote() {
  return (
    <div className="mb-5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
      <p className="font-medium">Say whose wages these are</p>
      <p className="mt-1 text-muted-foreground">
        This joint plan has wages and business income but doesn't say which spouse owns the business. Until you choose, the wages are treated as the other spouse's, so self-employment tax and any S corporation payroll tax use the owner's full Social Security wage base (the higher figure). Enter each spouse's wages under Figures and choose the owner under Business details.
      </p>
    </div>
  );
}

/** Shown on an open plan computed before the latest engine corrections, with the ones that apply to it. */
function CorrectionsNote({ items }: { items: NonNullable<PlanDetail["corrections"]> }) {
  return (
    <div className="mb-5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
      <p className="font-medium">Figures changed: calculations corrected on {formatDate(PLANNING_CORRECTED_ON)}</p>
      <p className="mt-1 text-muted-foreground">
        ClientFlow corrected how {["one rule is", "two rules are", "three rules are"][items.length - 1] ?? `${items.length} rules are`} worked out, and this plan's figures now follow the law. Check them before you send the plan for review. Approved and shared plans keep the figures the client was given.
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
        {items.map((c) => (
          <li key={c.key}>{c.text}</li>
        ))}
      </ul>
    </div>
  );
}

// --- Scenarios ------------------------------------------------------------------------------

function StrategyPicker({ years, baseYear, onAdd, onClose }: { years: number; baseYear: number; onAdd: (s: ScenarioStrategy) => void; onClose: () => void }) {
  const [key, setKey] = useState<StrategyKey | null>(null);
  const def = STRATEGIES.find((x) => x.key === key);
  const [params, setParams] = useState<Record<string, number>>({});
  const [yrs, setYrs] = useState<number[]>(Array.from({ length: years }, (_, i) => i));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{def ? def.title : "Add a strategy"}</DialogTitle>
        </DialogHeader>
        {!def ? (
          <ul className="grid gap-2 sm:grid-cols-2">
            {STRATEGIES.map((s) => (
              <li key={s.key}>
                <button
                  className="h-full w-full rounded-lg border border-border p-3 text-left hover:border-primary/40 hover:bg-primary/5"
                  onClick={() => {
                    setKey(s.key);
                    setParams(Object.fromEntries(s.params.map((x) => [x.key, x.default])));
                  }}
                >
                  <span className="block text-sm font-medium">{s.title}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">{s.fits}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="space-y-3 text-sm">
            <p>{def.summary}</p>
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Check first:</span> {def.checks}
            </p>
            {def.params.map((pm) => (
              <div key={pm.key} className="space-y-1">
                <Label htmlFor={`sp-${pm.key}`}>{pm.label}</Label>
                {pm.options ? (
                  <select id={`sp-${pm.key}`} className={select} value={params[pm.key]} onChange={(e) => setParams({ ...params, [pm.key]: Number(e.target.value) })}>
                    {pm.options.map((o) => (
                      <option key={o} value={o}>
                        {o}%
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input id={`sp-${pm.key}`} inputMode="decimal" value={params[pm.key] ?? ""} onChange={(e) => setParams({ ...params, [pm.key]: Number(e.target.value.replace(/[$,]/g, "")) || 0 })} />
                )}
              </div>
            ))}
            <div className="space-y-1">
              <p className="font-medium">Years</p>
              <div className="flex flex-wrap gap-3">
                {Array.from({ length: years }, (_, i) => (
                  <label key={i} className="flex items-center gap-1.5">
                    <input type="checkbox" checked={yrs.includes(i)} onChange={(e) => setYrs(e.target.checked ? [...yrs, i].sort() : yrs.filter((y) => y !== i))} /> {baseYear + i}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setKey(null)}>
                Back
              </Button>
              <Button disabled={!yrs.length} onClick={() => onAdd({ key: def.key, params, years: yrs })}>
                Add to scenario
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ScenarioCard({ sc, plan, baseline, canEdit, color }: { sc: Scenario; plan: PlanDetail["plan"]; baseline: Scenario; canEdit: boolean; color: string }) {
  const [picking, setPicking] = useState(false);
  const save = useMutation({ mutationFn: (body: Record<string, unknown>) => api.send("PATCH", `/api/tax-plan-scenarios/${sc.id}`, body) });
  const remove = useMutation({ mutationFn: () => api.send("DELETE", `/api/tax-plan-scenarios/${sc.id}`) });
  const recommend = useMutation({ mutationFn: () => api.send("PATCH", `/api/tax-plans/${plan.id}`, { recommendedScenarioId: sc.id }) });
  const savings = baseline.total - sc.total;
  const recommended = plan.recommendedScenarioId === sc.id;
  return (
    <div className={cn("flex min-w-0 flex-col rounded-lg border bg-card", recommended ? "border-primary ring-1 ring-primary" : "border-card-border")}>
      <div className="flex items-start gap-2 border-b border-border px-4 py-3">
        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{sc.name}</p>
          <p className="text-xs text-muted-foreground">
            {usd(sc.total)} over {plan.years} year{plan.years === 1 ? "" : "s"}
          </p>
        </div>
        {sc.position > 0 && (
          <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular", savings >= 0 ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200" : "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200")}>
            {savings >= 0 ? `Saves ${usd(savings)}` : `Costs ${usd(-savings)}`}
          </span>
        )}
      </div>
      <ul className="flex-1 space-y-1.5 px-4 py-3 text-sm">
        {sc.position === 0 ? (
          <li className="text-muted-foreground">Today's figures, carried forward with no changes.</li>
        ) : !sc.strategies.length ? (
          <li className="text-muted-foreground">No strategies yet.</li>
        ) : (
          sc.strategies.map((st, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="min-w-0 flex-1">
                {STRATEGIES.find((x) => x.key === st.key)?.title}
                <span className="block text-xs text-muted-foreground">{st.years.map((y) => plan.baseYear + y).join(", ")}</span>
              </span>
              {canEdit && (
                <button aria-label="Remove strategy" className="text-muted-foreground hover:text-foreground" onClick={() => save.mutate({ strategies: sc.strategies.filter((_, j) => j !== i) })}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))
        )}
      </ul>
      {canEdit && (
        <div className="flex flex-wrap gap-1 border-t border-border px-3 py-2">
          {sc.position > 0 && (
            <Button size="sm" variant="ghost" onClick={() => setPicking(true)}>
              <Plus className="h-3.5 w-3.5" /> Strategy
            </Button>
          )}
          <Button size="sm" variant={recommended ? "default" : "ghost"} onClick={() => recommend.mutate()} disabled={recommended}>
            {recommended ? "Recommended" : "Recommend"}
          </Button>
          {sc.position > 0 && (
            <Button size="sm" variant="ghost" aria-label="Delete scenario" className="ml-auto" onClick={async () => (await ask({ title: `Delete "${sc.name}"?`, confirmLabel: "Delete", destructive: true })) && remove.mutate()}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
      {picking && (
        <StrategyPicker
          years={plan.years}
          baseYear={plan.baseYear}
          onClose={() => setPicking(false)}
          onAdd={(s) => {
            save.mutate({ strategies: [...sc.strategies, s] });
            setPicking(false);
          }}
        />
      )}
    </div>
  );
}

/** The state's current-law notes, once each, from the first year they apply. */
function stateNotes(sc: Scenario): Array<{ text: string; from: number }> {
  const out = new Map<string, number>();
  for (const r of sc.results) if (r.state.note && !out.has(r.state.note)) out.set(r.state.note, r.year);
  return [...out].map(([text, from]) => ({ text, from }));
}

function YearTable({ sc, tables }: { sc: Scenario; tables: PlanDetail["tables"] }) {
  const rows: Array<[string, (r: YearResult) => string, boolean?]> = [
    ["Total income", (r) => usd(r.federal.totalIncome)],
    ["Adjusted gross income", (r) => usd(r.federal.agi)],
    ["Deduction", (r) => `${usd(r.federal.deduction)}${r.federal.itemizing ? " (itemized)" : ""}`],
    ["QBI deduction", (r) => usd(r.federal.qbiDeduction)],
    ["Senior, tips and overtime", (r) => usd(r.federal.seniorDeduction + r.federal.tipsDeduction + r.federal.overtimeDeduction)],
    ["Taxable income", (r) => usd(r.federal.taxableIncome), true],
    ["Income tax", (r) => usd(r.federal.regularTax)],
    ["AMT", (r) => usd(r.federal.amt)],
    ["Self-employment tax", (r) => usd(r.federal.seTax)],
    // An S corporation owner's salary: Social Security and Medicare, both halves. A real cost of the election, paid
    // through payroll (Form 941), so it is in Federal tax but not in the estimated payments.
    ...(sc.results.some((r) => r.federal.payrollTaxes) ? [["Payroll tax on the owner's salary (both halves)", (r: YearResult) => usd(r.federal.payrollTaxes)] as [string, (r: YearResult) => string]] : []),
    ["NIIT and Additional Medicare", (r) => usd(r.federal.niit + r.federal.additionalMedicare)],
    ["Credits", (r) => (r.federal.credits + r.federal.refundableCredits ? `−${usd(r.federal.credits + r.federal.refundableCredits)}` : usd(0))],
    ["Federal tax", (r) => usd(r.federal.totalTax), true],
    ["State and local tax", (r) => usd(r.state.totalTax)],
    ["Total tax", (r) => usd(r.total), true],
    ["Bracket", (r) => `${roundHalfAwayFromZero(r.federal.bracketRate * 100)}%`],
    ["Marginal rate (all-in, federal)", (r) => pct(r.federal.marginalRate)],
    ["Effective rate", (r) => pct(r.total / Math.max(1, r.federal.totalIncome))],
    // The 1040-ES installments: three equal whole-dollar payments, the remainder in the fourth (the set-aside's figures).
    ["Federal estimate per quarter", (r) => usd(r.estimates.perQuarter)],
    ...(sc.results.some((r) => r.stateEstimates || ["MD", "VA", "DC"].includes(r.state.code))
      ? [["State estimate per quarter", (r: YearResult) => (r.stateEstimates ? usd(r.stateEstimates.perQuarter) : "Enter withholding")] as [string, (r: YearResult) => string]]
      : []),
  ];
  return (
    <TableScroll label={`${sc.name} by year table`}>
      <table className="w-full min-w-[480px] text-sm">
        <thead>
          <tr className="border-b border-border text-xs text-muted-foreground">
            <th className="py-2 pr-3 text-left font-medium">{sc.name}</th>
            {sc.results.map((r) => (
              <th key={r.year} className="py-2 pl-3 text-right font-medium tabular">
                {r.year}
                {(tables.find((t) => t.year === r.year)?.estimated || r.state.estimated) && <span className="block text-[10px] font-normal">estimated tables</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, fn, strong]) => (
            <tr key={label} className={cn("border-b border-border/60", strong && "font-semibold")}>
              <td className="py-1.5 pr-3">{label}</td>
              {sc.results.map((r) => (
                <td key={r.year} className="py-1.5 pl-3 text-right tabular">
                  {fn(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {stateNotes(sc).length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
          {stateNotes(sc).map((n) => (
            <li key={n.text}>
              <span className="font-medium text-foreground">{n.from} and later:</span> {n.text}
            </li>
          ))}
        </ul>
      )}
      {sc.results.some((r) => r.strategyNotes.length) && (
        <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
          {sc.results.map((r) =>
            r.strategyNotes.map((n, i) => (
              <li key={`${r.year}-${i}`}>
                <span className="font-medium text-foreground">{r.year}:</span> {n}
              </li>
            )),
          )}
        </ul>
      )}
    </TableScroll>
  );
}

// --- The plan workspace -----------------------------------------------------------------------

/**
 * The summary the client reads first. "Draft with Claude" writes one from the plan's own figures
 * (every number is filled in by ClientFlow, not typed by the AI); staff edit it, then save.
 */
function PlanSummary({ planId, saved, status, canEdit }: { planId: string; saved: string; status: string; canEdit: boolean }) {
  const [text, setText] = useState(saved);
  // A figure that isn't one of the plan's own is refused until staff confirm they checked it.
  const save = useMutation({ meta: { inlineError: true }, mutationFn: (v: { summary: string; confirmNumbers?: string[] }) => api.send("PATCH", `/api/tax-plans/${planId}`, v) });
  const onSave = (summary: string) => save.mutate({ summary });
  const toConfirm = save.error instanceof ApiError && save.error.code === "unmatched_numbers" ? ((save.error.details as { unmatched?: string[] } | undefined)?.unmatched ?? []) : [];
  const [drafted, setDrafted] = useState(false);
  useEffect(() => {
    setText(saved);
    setDrafted(false);
  }, [saved]);
  const draft = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ summary: string }>("POST", `/api/tax-plans/${planId}/summary/draft`, {}),
    onSuccess: (r) => {
      setText(r.summary);
      setDrafted(true);
    },
  });
  const dirty = text !== saved;
  return (
    <Section
      title="Summary for the client"
      description="Shown at the top of the plan in the portal. Plain words: what to do and why."
      action={
        canEdit && (
          <Button
            size="sm"
            variant="outline"
            disabled={draft.isPending}
            onClick={async () => {
              if (text.trim() && !(await ask({ title: "Replace the summary with a draft?", description: `Claude writes a new summary from the plan's figures. What is in the box now is replaced (nothing is saved until you save).${status !== "draft" ? " Saving a new summary sends the plan back to draft, so it is reviewed again before the client sees it." : ""}`, confirmLabel: "Draft with Claude" }))) return;
              draft.mutate();
            }}
          >
            <Sparkles className="h-3.5 w-3.5" /> {draft.isPending ? "Drafting…" : "Draft with Claude"}
          </Button>
        )
      }
    >
      <Textarea aria-label="Summary for the client" rows={7} disabled={!canEdit} value={text} onChange={(e) => setText(e.target.value)} onBlur={() => !drafted && dirty && onSave(text)} />
      {drafted && <p className="mt-2 text-xs text-muted-foreground">Draft ready: every number in it was filled in by ClientFlow from the plan's figures. Read it, edit it, then save. It isn't shared until the plan is.</p>}
      {draft.error && <p className="mt-2 text-sm text-destructive">{errorMessage(draft.error)}</p>}
      {save.error && <p className="mt-2 text-sm text-destructive">{errorMessage(save.error)}</p>}
      {canEdit && toConfirm.length > 0 && (
        <Button size="sm" variant="outline" className="mt-2" disabled={save.isPending} onClick={() => save.mutate({ summary: text, confirmNumbers: toConfirm })}>
          I have checked these figures: save
        </Button>
      )}
      {canEdit && drafted && (
        <div className="mt-3 flex justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setText(saved);
              setDrafted(false);
            }}
          >
            Discard draft
          </Button>
          <Button size="sm" onClick={() => onSave(text)} disabled={!text.trim()}>
            Save summary
          </Button>
        </div>
      )}
    </Section>
  );
}

export function TaxPlanPage({ id, me }: { id: string; me: Me }) {
  const { data, isLoading, error } = useQuery<PlanDetail>({ queryKey: [`/api/tax-plans/${id}`] });
  const { data: library } = useQuery<Library>({ queryKey: ["/api/tax-planning/library"] });
  const names = useMemberNames();
  const [, go] = useLocation();
  const [detail, setDetail] = useState<string | null>(null);
  const act = useMutation({ meta: { inlineError: true }, mutationFn: (v: { path: string; body?: unknown }) => api.send("POST", `/api/tax-plans/${id}/${v.path}`, v.body) });
  const patch = useMutation({ mutationFn: (body: Record<string, unknown>) => api.send("PATCH", `/api/tax-plans/${id}`, body) });
  const addScenario = useMutation({ meta: { inlineError: true }, mutationFn: (name: string) => api.send("POST", `/api/tax-plans/${id}/scenarios`, { name }) });
  const toEstimates = useMutation({
    meta: { inlineError: true },
    mutationFn: (v: { year: number; projected: number; withholding: number }) =>
      api.send("POST", `/api/clients/${data!.plan.clientId}/tax/estimates`, { taxYear: v.year, jurisdiction: "federal", projectedTaxCents: v.projected * 100, priorYearTaxCents: data!.plan.priorYearTax !== null ? data!.plan.priorYearTax * 100 : null, priorYearAgiCents: data!.plan.priorYearAgi !== null ? data!.plan.priorYearAgi * 100 : null, withholdingCents: v.withholding * 100, notes: `From the tax plan "${data!.plan.title}"` }),
    onSuccess: () => go(`/clients/${data!.plan.clientId}?tab=tax`),
  });
  const remove = useMutation({ mutationFn: () => api.send("DELETE", `/api/tax-plans/${id}`), onSuccess: () => go("/tax/planning") });
  if (isLoading) return <Loading />;
  if (error || !data) return <Empty>Plan not found.</Empty>;
  const p = data.plan;
  const canEdit = hasPermission(me, "engagements.write") && p.status !== "accepted";
  const baseline = data.scenarios.find((s) => s.position === 0)!;
  const rec = data.scenarios.find((s) => s.id === p.recommendedScenarioId);
  const shown = data.scenarios.find((s) => s.id === detail) ?? rec ?? baseline;
  const chart = baseline.results.map((r, i) => ({ year: String(r.year), ...Object.fromEntries(data.scenarios.map((s) => [s.name, s.results[i]?.total ?? 0])) }));
  const author = p.createdByUserId === me.user?.id;

  return (
    <>
      <PageHeader
        crumbs={[
          { label: "Tax", href: "/tax" },
          { label: "Tax Planning", href: "/tax/planning" },
          { label: data.clientName, href: `/clients/${p.clientId}?tab=tax` },
        ]}
        title={p.title}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Link href={`/clients/${p.clientId}?tab=tax`} className="font-medium text-foreground hover:underline">
              {data.clientName}
            </Link>
            <span aria-hidden>·</span>
            <span>
              {p.baseYear}
              {p.years > 1 && `–${p.baseYear + p.years - 1}`}
            </span>
            <PlanStatusBadge status={p.status} />
          </span>
        }
        actions={
          hasPermission(me, "engagements.write") && (
            <div className="flex flex-wrap gap-2">
              {p.status === "draft" && !p.questionnaire?.sentAt && (
                <Button variant="outline" onClick={() => act.mutate({ path: "questionnaire" })}>
                  Send questionnaire
                </Button>
              )}
              {p.status === "draft" && <Button onClick={() => act.mutate({ path: "submit" })}>Send for review</Button>}
              {p.status === "in_review" && !author && <Button onClick={() => act.mutate({ path: "approve" })}>Approve plan</Button>}
              {(p.status === "in_review" || p.status === "approved") && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    const note = await askText({ title: "Send back for changes", label: "What needs to change", required: true });
                    if (note) act.mutate({ path: "return", body: { note } });
                  }}
                >
                  Send back
                </Button>
              )}
              {p.status === "approved" && <Button onClick={() => act.mutate({ path: "share" })}>Share with client</Button>}
              {(p.status === "draft" || p.status === "in_review" || p.status === "approved") && (
                <Button variant="ghost" aria-label="Delete plan" onClick={async () => (await ask({ title: "Delete this plan?", confirmLabel: "Delete", destructive: true })) && remove.mutate()}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )
        }
      />
      {act.error && <p className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-2 text-sm text-destructive">{errorMessage(act.error)}</p>}
      {p.status === "in_review" && author && <p className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">Waiting for another preparer to review and approve the plan.</p>}
      {p.status === "accepted" && (
        <p className="mb-4 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm dark:border-emerald-900 dark:bg-emerald-950/40">
          Accepted by {p.acceptedName} on {formatDateTime(p.acceptedAt!)}. The recommendations are now the client's action items.
        </p>
      )}
      {(p.status === "approved" || p.status === "shared") && p.reviewedAt && (
        <p className="mb-4 text-sm text-muted-foreground">
          Approved by {names.get(p.reviewedByUserId ?? "") ?? "a reviewer"} on {formatDateTime(p.reviewedAt)}
          {p.sharedAt && `; shared ${formatDateTime(p.sharedAt)}`}. Any change sends it back for review.
        </p>
      )}

      {businessOwnerMissing(p.inputs.federal) && <OwnerNote />}
      {(data.corrections?.length ?? 0) > 0 && <CorrectionsNote items={data.corrections!} />}

      <StatRow
        stats={[
          { label: `Current path, ${p.years} yr`, value: usd(baseline.total) },
          { label: "Recommended", value: rec ? usd(rec.total) : "Not chosen", tone: rec ? "default" : "warning" },
          { label: "Difference", value: rec ? usd(baseline.total - rec.total) : "—", tone: rec && baseline.total - rec.total > 0 ? "success" : "default" },
          { label: `${p.baseYear} marginal rate`, value: pct((rec ?? baseline).results[0]?.federal.marginalRate ?? 0) },
        ]}
      />

      <div className="space-y-6">
        <Section
          title="Scenarios"
          description="The current path and up to four alternatives, each built from the strategy library."
          action={
            canEdit &&
            data.scenarios.length < 5 && (
              <Button
                size="sm"
                onClick={async () => {
                  const name = await askText({ title: "New scenario", label: "Name", required: true, placeholder: "e.g. Retirement savings and Roth" });
                  if (name) addScenario.mutate(name);
                }}
              >
                <Plus className="h-4 w-4" /> Scenario
              </Button>
            )
          }
        >
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {data.scenarios.map((sc, i) => (
              <ScenarioCard key={sc.id} sc={sc} plan={p} baseline={baseline} canEdit={canEdit} color={COLORS[i % COLORS.length]!} />
            ))}
          </div>
          {addScenario.error && <p className="mt-2 text-sm text-destructive">{errorMessage(addScenario.error)}</p>}
          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ left: 8, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="year" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis tickFormatter={(v) => `$${Math.round(v / 1000)}k`} tickLine={false} axisLine={false} fontSize={12} width={48} />
                <Tooltip formatter={(v: number) => usd(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {data.scenarios.map((s, i) => (
                  <Bar key={s.id} dataKey={s.name} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} maxBarSize={28} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section
          title="Year by year"
          description={
            data.tables.some((t) => t.estimated) || shown.results.some((r) => r.state.estimated)
              ? "Years after the latest published IRS (or Maryland) figures use tables indexed by the inflation assumption; they're marked estimated."
              : undefined
          }
          action={
            <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={shown.id} onChange={(e) => setDetail(e.target.value)} aria-label="Scenario">
              {data.scenarios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          }
        >
          <YearTable sc={shown} tables={data.tables} />
          {canEdit && shown.results[0] && (
            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-4 text-sm">
              <span className="text-muted-foreground">
                Set up {shown.results[0].year} federal estimated payments from this projection ({usd(shown.results[0].estimates.perQuarter)} a quarter after withholding
                {shown.results[0].estimates.installmentsCents[3] !== shown.results[0].estimates.installmentsCents[0] && `, the last ${usdCents(shown.results[0].estimates.installmentsCents[3] / 100)}`}).
              </span>
              {/* The 1040's tax: payroll tax on an S corporation salary is paid through payroll, not estimates. */}
              <Button size="sm" variant="outline" disabled={toEstimates.isPending} onClick={() => toEstimates.mutate({ year: shown.results[0]!.year, projected: shown.results[0]!.federal.totalTax - shown.results[0]!.federal.payrollTaxes, withholding: p.inputs.federal.withholding })}>
                Create estimate plan
              </Button>
              {toEstimates.error && <span className="text-destructive">{errorMessage(toEstimates.error)}</span>}
            </div>
          )}
        </Section>

        <Figures d={data} canEdit={canEdit} />

        <div className="grid gap-6 lg:grid-cols-2">
          <PlanSummary planId={p.id} saved={p.summary} status={p.status} canEdit={canEdit && p.status !== "accepted"} />
          <Section title="Assumptions">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="as-years">Years</Label>
                <select id="as-years" className={select} disabled={!canEdit} value={p.years} onChange={(e) => patch.mutate({ years: Number(e.target.value) })}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="as-g">Income growth %</Label>
                <BlurInput id="as-g" inputMode="decimal" disabled={!canEdit} value={String(p.incomeGrowthPct)} onCommit={(text) => patch.mutate({ incomeGrowthPct: Number(text) || 0 })} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="as-i">Inflation %</Label>
                <BlurInput id="as-i" inputMode="decimal" disabled={!canEdit} value={String(p.inflationPct)} onCommit={(text) => patch.mutate({ inflationPct: Number(text) || 0 })} />
              </div>
            </div>
            <ul className="mt-4 space-y-1 text-xs text-muted-foreground">
              {data.tables.map((t) => (
                <li key={t.year}>
                  <span className="font-medium text-foreground">{t.year}:</span> {t.sources.join("; ")}
                </li>
              ))}
              {(library?.limitations ?? []).map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
}
